import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import VuexPersistence from 'vuex-persist';

import { RootState } from './types';
import { globalState } from './global';
import { infosPSBEN } from './infosPSBEN';
import { referentielCategoriesLieu } from './referentielCategoriesLieu';
import { referentielVaccins } from './referentielVaccins';
import { infosVaccinationFolder } from './folder';
import { tableauVaccinations } from './tableauVaccinations';
import { codesPostaux } from './codePostal';
import { motifsInjectionSupp } from './motifInjectionsupp';
import { motifsSuppression } from './motifsSuppression';
import { centreVaccination } from './centreVaccination';
import { vaccinations } from './vaccinations';
import { parametresUtilisateur } from './parametresUtilisateur';
import { codesScolaires } from './codeScolaire';
import { messageAccueilCms } from './cms/messageAccueil';
import { mdiConsoleLine } from '@mdi/js';

declare const PERSIST_DELAY_DAYS: number;
const persistDelay = typeof PERSIST_DELAY_DAYS !== 'undefined' ? PERSIST_DELAY_DAYS : 0;

/**
 * Met une date au début de journée (minuit)
 * @param date Date à modifier
 */
const dateToStart = (date: Date): Date => {
	const newDate: Date = new Date(date);
	newDate.setHours(0, 0, 0, 0);
	return newDate;
};

/**
 * Compare le nombre de jours entre 2 dates
 * @param date1 date 1
 * @param date2 date 2
 */
const diffDays = (date1: Date, date2: Date) =>
	Math.abs(Math.floor((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24)));

/**
 * Vérifie l'expiration de persistence
 * @param date Date de la persistence
 */
const isExpired = (date: string) => diffDays(dateToStart(new Date(date)), dateToStart(new Date())) > persistDelay;

/** The store is saved in the browser's session */
const vuexLocal = new VuexPersistence<RootState>({
	restoreState: (key, storage) => {
		const data = window.localStorage.getItem('vacci-state');
		if (data) {
			try {
				const storedParameters = JSON.parse(decodeURIComponent(escape(atob(data))), reviver);
				if (!isExpired(storedParameters.date)) {
					return storedParameters.state;
				}
			} catch (e) {
				// erreur de parsing (si un storage est déjà présent mais pas dans le bon format)
				return null;
			}
		}
		return null;
	},
	saveState: (key, state, storage) => {
		window.localStorage.setItem(
			'vacci-state',
			btoa(unescape(encodeURIComponent(
				JSON.stringify({
					state,
					date: new Date()
				}, replacer)
			)))
		);
	},
	modules: ['parametresUtilisateur']
});

function replacer(key: any, value: any) {
	if (value instanceof Map) {
		return {
			dataType: 'Map',
			value: Array.from(value.entries())
		};
	} else {
		return value;
	}
}
function reviver(key: any, value: any) {
	if (typeof value === 'object' && value !== null) {
		if (value.dataType === 'Map') {
			return new Map(value.value);
		}
	}
	return value;
}

Vue.use(Vuex);

/** See https://vuex.vuejs.org/fr/getting-started.html for help */
const storeOptions: StoreOptions<RootState> = {
	strict: true,
	state: {},
	// See https://vuex.vuejs.org/guide/modules.html for more info on modules
	modules: {
		globalState,
		infosPSBEN,
		referentielCategoriesLieu,
		referentielVaccins,
		infosVaccinationFolder,
		tableauVaccinations,
		codesPostaux,
		centreVaccination,
		vaccinations,
		parametresUtilisateur,
		codesScolaires,
		motifsInjectionSupp,
		motifsSuppression,
		messageAccueilCms
	},
	plugins: [
		vuexLocal.plugin
	]
};

export const store = new Vuex.Store<RootState>(storeOptions);
